import {Dialog, DialogActions, DialogContent, DialogTitle, Link as MuiLink, Typography} from '@material-ui/core';
import React, {useCallback, useEffect, useState} from 'react';
import UserRoleSelector from './UserRoleSelector';
import {HoopsTextField} from '../HoopsTextField';
import {useUpdateUser, useUsersCache} from '../../hooks/api';
import {hoopsQuerySetRefetch} from '../../actions/hoopsQuery';
import {useDispatch} from 'react-redux';
import EmailTemplateService from '../../servicesOld/EmailTemplateService';
import {Column, Row} from '../../componentsLib/Layout';
import {Button, ImageThumbnail, UserAvatar} from '../../componentsLib/Basic';
import {registerGlobalStyle} from '../../theme';
import {ColorPicker} from '../../componentsLib/Pickers';
import {getCurrentUser} from '../../actions/action-types';
import {BodyText, CaptionText} from '../../componentsLib/Text';
import {ImageAndFileUploadManager} from '../../componentsLib/DragDrop';
import {MaxFileSize, stringToColor} from '../../utils';
import {useS3UploadCache} from '../../hooks';

registerGlobalStyle('.user-edit', (theme) => ({
  '.modal-dialog-content': {maxWidth: 800, minWidth: 500},
  '.avatar': {
    marginBottom: theme.spacing(2),
    '.user-avatar': {
      width: 120,
      height: 120,
      fontSize: theme.fontSize(60),
    },
    '.avatar-settings': {
      alignSelf: 'center',
      gap: theme.spacing(1),
    },
    '.image-upload-manager': {
      width: '100%',
      '.image-selector': {padding: 0},
      '.file-select': {
        height: 'unset',
        border: 'none',
        display: 'block',
        zIndex: 2,
      },
    },
    '.image-uploader, .image-file': {
      height: 35,
      '.text-body': {color: theme.colors.text.main}
    },
    '.image-uploader': {
      position: 'relative',
      '.text-body': {
        position: 'absolute',
        left: 40,
        fontSize: theme.fontSize(16)
      },
      '&:hover': {'.text-body': {color: theme.colors.text.highlight}}
    },
  },
  '.image-file': {'.MuiSvgIcon-root': {color: theme.colors.palette.greyLighter,}},
  '.dialog-actions': {width: '100%'},
}));

export const UserEditModal = ({user = {}, open, title, roleSelector = false, onClose = () => null}) => {
  const [updatedUser, setUpdatedUser] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const dispatch = useDispatch();

  const [color, setColor] = useState(user.avatar?.color ?? stringToColor(user?.fullName));
  const [image, setImage] = useState(user.avatar?.image ?? null);
  const {refetch: refetchCachedUsers} = useUsersCache();
  const uploadCache = useS3UploadCache();

  const updateUserForm = useCallback(({target: {name, value}}) => {
    if (value === '') {
      setFormErrors((prev) => ({...prev, [name]: 'This is a required field'}));
    } else if (name === 'username' && !EmailTemplateService.validateEmail(value)) {
      setFormErrors((prev) => ({...prev, [name]: 'Email is invalid'}));
    } else {
      setFormErrors((prev) => {
        if (prev[name]) {
          const clone = {...prev};
          delete clone[name];
          return clone;
        }
        return prev;
      });
    }

    setUpdatedUser((prev) => {
      if (user[name] === value) {
        const clone = {...prev};
        delete clone[name];
        return clone;
      }
      return {...prev, [name]: value};
    });
  }, [user]);

  useEffect(() => {
    if (uploadCache.files.length > 0) {
      setImage(uploadCache.files[0].url);
      updateUserForm({target: {name: 'avatar.image', value: uploadCache.files[0].url}});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadCache]);

  const handleDeleteImage = useCallback(() => {
    setImage(null);
    updateUserForm({target: {name: 'avatar.image', value: null}});
    if (uploadCache.files.length > 0) {
      uploadCache.clearCache();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadCache]);

  const onChangeColor = useCallback((newColor) => {
    setColor(newColor);
    updateUserForm({target: {name: 'avatar.color', value: newColor}});
  }, [updateUserForm]);

  const handleClose = useCallback(() => {
    setFormErrors({});
    setImage(null);
    onClose(false);
    setUpdatedUser({});
  }, [onClose]);

  const {update: saveUpdatedUser, isSaving} = useUpdateUser({errorMessage: ({error, status}) => status === 409 ? error.response.data.message : null});

  const submit = useCallback(async () => {
    // api update user
    await saveUpdatedUser({id: user._id, user: updatedUser});
    dispatch(hoopsQuerySetRefetch('user', true));
    if (!roleSelector) {
      dispatch(getCurrentUser());
    }
    refetchCachedUsers();
    handleClose();
  }, [dispatch, handleClose, refetchCachedUsers, roleSelector, saveUpdatedUser, updatedUser, user._id]);

  return (
    <Dialog open={open} fullWidth maxWidth={roleSelector ? 'md' : 'sm'} className={'user-edit'} onClose={handleClose}>
      <DialogTitle onClick={handleClose} disableTypography={true} >
        <h3>{title}</h3>
      </DialogTitle>
      <DialogContent data-intercom-target={'invite-user-screen'} style={{overflowY: 'hidden'}}>
        {user &&
          <Column>

            {/* Avatar settings */}
            <Row gap className={'avatar'}>
              <UserAvatar name={user.fullName} color={color} image={image} />
              <Column fillWidth className={'avatar-settings'}>

                <Column>
                  <CaptionText>Image</CaptionText>
                  {!image &&
                    <Row gap alignCenter className={'image-uploader'}>
                      <ImageAndFileUploadManager
                        dropZoneFirst={false}
                        files={[]}
                        value={''}
                        maxSize={MaxFileSize.Max2Meg}
                        uploadCache={uploadCache}
                      />
                      <BodyText>Click to Upload</BodyText>
                    </Row>
                  }
                  {image &&
                    <Row gap alignCenter className={'image-file'}>
                      <ImageThumbnail
                        imageUrl={image}
                        hoverZoom
                        size={24}
                      />
                      <Button prefix={'delete'} onClick={() => handleDeleteImage()} />
                    </Row>
                  }
                </Column>

              </Column>
            </Row>

            <Column>
              <ColorPicker
                button={<div className={'color-button'} style={{background: color, boxShadow: '0 0 0 1px rgba(0, 0, 0, .1)'}} />}
                color={color}
                label={'Color'}
                onChange={onChangeColor}
                inplaceEdit
              />
            </Column>

            {/* User settings */}
            <HoopsTextField
              name='firstName'
              label='First Name'
              defaultValue={user.firstName}
              onChange={updateUserForm}
              error={formErrors.firstName}
              helperText={formErrors.firstName}
            />
            <HoopsTextField
              name='lastName'
              label='Last Name'
              defaultValue={user.lastName}
              onChange={updateUserForm}
              error={formErrors.lastName}
              helperText={formErrors.lastName}
            />
            <HoopsTextField
              name='username'
              label='Email'
              defaultValue={user.username}
              onChange={updateUserForm}
              error={formErrors.username}
              helperText={formErrors.username}
            />

            {/* edit roles */}
            {roleSelector &&
              <>
                <div>
                  <Typography color={'textSecondary'} variant={'body1'} gutterBottom>Permission</Typography>
                  <UserRoleSelector
                    input
                    initialRole={user.role}
                    onChange={(value) => updateUserForm({target: {name: 'role', value: value}})}
                  />
                </div>
                <div>
                  <Typography variant={'body1'} gutterBottom>Want the full breakdown of user access?
                    <MuiLink color={'primary'} underline={'none'} href={'http://help.hoopscrm.com/en/articles/5026846-user-roles-permissions'} target='_blank'>
                      &nbsp;Click Here
                    </MuiLink>
                  </Typography>
                </div>
              </>
            }
          </Column>
        }
      </DialogContent>
      <DialogActions>
        <Row justifySpaceBetween className={'dialog-actions'} pad>
          <Button navNegative onClick={handleClose}>Cancel</Button>
          <Button navPositive loading={isSaving} onClick={() => submit()} disabled={Object.keys(updatedUser).length < 1 || Object.keys(formErrors).length > 0}>Save</Button>
        </Row>
      </DialogActions>
    </Dialog>

  );
};
